import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { colors } from "../../theme";

const DRAWER_WIDTH = 240;
const TOPBAR_HEIGHT = 60;

function getStoredUser() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem("catalog_user");
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function userInitials(user) {
  if (!user) return "U";
  const first = (user.firstName ?? "").trim();
  const last = (user.lastName ?? "").trim();
  if (first || last) {
    return `${first[0] ?? ""}${last[0] ?? ""}`.toUpperCase() || "U";
  }
  const email = (user.email ?? "").trim();
  return email ? email[0].toUpperCase() : "U";
}

/**
 * Application chrome — top bar + side drawer + main content slot.
 *
 * Receives the route content as `children`. Nav items and the hide-chrome
 * decision (e.g. on /login) are decided by the caller so the shell stays
 * route-agnostic.
 */
export default function AppShell({ navItems, hideChrome, children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(() => getStoredUser());

  useEffect(() => {
    setUser(getStoredUser());
  }, [location.pathname]);

  const handleLogout = () => {
    if (typeof window !== "undefined") {
      localStorage.removeItem("token");
      localStorage.removeItem("auth_token");
      localStorage.removeItem("catalog_user");
    }
    setUser(null);
    navigate("/login", { replace: true });
  };

  const initials = useMemo(() => userInitials(user), [user]);
  const displayName = useMemo(() => {
    if (!user) return null;
    const composed = [user.firstName, user.lastName].filter(Boolean).join(" ");
    return composed || user.email || null;
  }, [user]);

  if (hideChrome) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        {children}
      </Box>
    );
  }

  const drawerContent = (
    <Box
      sx={{
        width: DRAWER_WIDTH,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Toolbar
        variant="dense"
        sx={{
          minHeight: TOPBAR_HEIGHT,
          px: 2.5,
          borderBottom: `1px solid ${colors.border.hairline}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 2,
              bgcolor: colors.brand.purple,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: 0.5,
            }}
          >
            P
          </Box>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            Catalog
          </Typography>
        </Stack>
      </Toolbar>
      <List sx={{ flex: 1, px: 1, py: 1.5 }}>
        {navItems.map((item) => {
          const selected =
            location.pathname === item.path ||
            (item.match && item.match.test(location.pathname));
          return (
            <ListItemButton
              key={item.path}
              component={Link}
              to={item.path}
              selected={selected}
              onClick={() => setMobileOpen(false)}
              sx={{
                px: 1.5,
                py: 1,
                my: 0.25,
                color: selected ? colors.text.primary : colors.text.secondary,
                "& .MuiListItemIcon-root": {
                  color: selected ? colors.action.blue : colors.text.secondary,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  variant: "body2",
                  fontWeight: selected ? 600 : 500,
                }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", minWidth: 0 }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          // Recover the surface bg from theme default; ensures contrast
          // with content while staying flat.
          bgcolor: colors.bg.surface,
        }}
      >
        <Toolbar
          variant="dense"
          sx={{ minHeight: TOPBAR_HEIGHT, px: { xs: 1.5, sm: 2.5 } }}
        >
          <IconButton
            edge="start"
            onClick={() => setMobileOpen((prev) => !prev)}
            sx={{ mr: 1, display: { md: "none" } }}
            aria-label="Open navigation"
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            noWrap
            sx={{ display: { xs: "block", md: "none" } }}
          >
            PABS Catalog
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" alignItems="center" spacing={1.25}>
            {displayName && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: { xs: "none", sm: "block" }, maxWidth: 200 }}
                noWrap
              >
                {displayName}
              </Typography>
            )}
            <Avatar
              sx={{
                bgcolor: colors.brand.purpleTint,
                color: colors.brand.purple,
                width: 32,
                height: 32,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {initials}
            </Avatar>
            <Button
              size="small"
              variant="outlined"
              onClick={handleLogout}
              startIcon={<LogoutOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{ display: { xs: "none", sm: "inline-flex" } }}
            >
              Sign out
            </Button>
            <IconButton
              size="small"
              onClick={handleLogout}
              sx={{ display: { xs: "inline-flex", sm: "none" } }}
              aria-label="Sign out"
            >
              <LogoutOutlinedIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
        aria-label="Primary"
      >
        <Drawer
          variant="temporary"
          open={!isDesktop && mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              borderRight: `1px solid ${colors.border.hairline}`,
            },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
              borderRight: `1px solid ${colors.border.hairline}`,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          bgcolor: "background.default",
          minHeight: "100vh",
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: TOPBAR_HEIGHT }} />
        {children}
      </Box>
    </Box>
  );
}
